import { hasDisplayText } from "./displayText.js";
import { getLocaleMessage } from "./localeCatalog.js";

const THAI_SECTION_SUFFIXES = {
  ทวิ: "bis",
  ตรี: "ter",
  จัตวา: "quarter",
  เบญจ: "quinque",
  ฉ: "sex",
  สัตต: "septem",
  อัฏฐ: "octo",
  อัฎฐ: "octo",
  นว: "novem",
  ทศ: "decem",
  เอกาทศ: "undecim",
  ทวาทศ: "duodecim",
  เตรส: "tredecim",
  จตุทศ: "quattuordecim",
};

const SECTION_TYPE_KEYS = {
  applicable_law: "applicableLaw",
  question: "question",
  holding: "holding",
  page: "page",
  preamble: "preamble",
  provision: "provision",
  parties: "parties",
  issue: "issue",
  source_attribution: "sourceAttribution",
};

const SEMANTIC_HEADINGS = {
  "ข้อกฎหมาย": "applicableLaw",
  "ข้อหารือ": "question",
  "แนววินิจฉัย": "holding",
  "แหล่งที่มา": "sourceAttribution",
  "Applicable law": "applicableLaw",
  Question: "question",
  Holding: "holding",
  "Source attribution": "sourceAttribution",
};

function headingText(segment) {
  return segment?.display_heading || segment?.heading || "";
}

export function sectionPresentation(segment, {
  locale = "th",
  language = locale,
  pairedSegment = null,
} = {}) {
  const uiLanguage = locale === "en" ? "en" : "th";
  const sourceLanguage = language === "en" ? "en" : "th";
  const key = stableAlignmentKey(segment);
  const pairedKey = stableAlignmentKey(pairedSegment);
  if (uiLanguage === "en" && key && key === pairedKey) {
    const pairedHeading = headingText(pairedSegment);
    if (pairedHeading) {
      return { text: pairedHeading, language: "en", isSemantic: false };
    }
  }

  const heading = headingText(segment);
  const semanticKey = SEMANTIC_HEADINGS[heading.trim()];
  if (semanticKey) {
    return {
      text: getLocaleMessage(uiLanguage, `reader.${semanticKey}`),
      language: uiLanguage,
      isSemantic: true,
    };
  }
  if (heading) return { text: heading, language: sourceLanguage, isSemantic: false };

  const typeKey = SECTION_TYPE_KEYS[segment?.type] || "sectionFallback";
  return {
    text: getLocaleMessage(uiLanguage, `reader.${typeKey}`),
    language: uiLanguage,
    isSemantic: true,
  };
}

export function segmentLabel(segment, locale = "th", options = {}) {
  return sectionPresentation(segment, { ...options, locale }).text;
}
export function sectionKey(segment) {
  const heading = segmentLabel(segment);
  const english = heading.match(
    /^Section\s+([0-9]+(?:\/[0-9]+)?)(?:\s+([A-Za-z]+))?/i,
  );
  if (english) {
    return `${english[1]} ${english[2]?.toLowerCase() || ""}`.trim();
  }
  const thai = heading.match(
    /^มาตรา\s*([0-9๐-๙]+(?:\/[0-9๐-๙]+)?)(?:\s+([^\s(]+))?/,
  );
  if (!thai) return null;
  const digits = thai[1].replace(/[๐-๙]/g, (digit) =>
    "๐๑๒๓๔๕๖๗๘๙".indexOf(digit),
  );
  const suffix = THAI_SECTION_SUFFIXES[thai[2]] || thai[2] || "";
  return `${digits} ${suffix}`.trim().toLowerCase();
}

/** Compare only on identifiers that are shared by the source contract. */
export function alignmentKey(segment) {
  return stableAlignmentKey(segment);
}

export function stableAlignmentKey(segment) {
  if (segment?.provision_id) return `provision:${segment.provision_id}`;
  if (segment?.structural_id) return `structure:${segment.structural_id}`;
  if (segment?.source_segment_id) return `source:${segment.source_segment_id}`;
  return null;
}

export function uniqueStableAlignmentMap(segments = []) {
  const unique = new Map();
  const ambiguous = new Set();
  segments.forEach((segment) => {
    const key = stableAlignmentKey(segment);
    if (!key || ambiguous.has(key)) return;
    if (unique.has(key)) {
      unique.delete(key);
      ambiguous.add(key);
      return;
    }
    unique.set(key, segment);
  });
  return unique;
}

export function resolveNavigationTarget(sourceBundle, targetBundle, targetSequence) {
  const sourceSegments = sourceBundle?.segments || [];
  const targetSegments = targetBundle?.segments || [];
  const sourceSegment = sourceSegments.find(
    (segment) => String(segment.sequence) === String(targetSequence),
  );
  if (!sourceSegment) return null;
  const sourceExpressionId = sourceBundle?.expression?.expression_id;
  const targetExpressionId = targetBundle?.expression?.expression_id;
  if (sourceBundle === targetBundle || (sourceExpressionId && sourceExpressionId === targetExpressionId)) {
    const targetSegment = targetSegments.find(
      (segment) => String(segment.sequence) === String(targetSequence),
    );
    return targetSegment?.sequence ?? null;
  }
  const key = stableAlignmentKey(sourceSegment);
  if (!key) return null;
  const uniqueSource = uniqueStableAlignmentMap(sourceSegments);
  const uniqueTarget = uniqueStableAlignmentMap(targetSegments);
  if (uniqueSource.get(key) !== sourceSegment) return null;
  return uniqueTarget.get(key)?.sequence ?? null;
}

export function commonSectionOptions(items, locale = "th") {
  const segmentSets = items.map((item) =>
    (item?.bundle?.segments || []).filter(
      (segment) => hasDisplayText(segment) && stableAlignmentKey(segment),
    ),
  );
  if (
    segmentSets.length < 2 ||
    segmentSets.some((segments) => !segments.length)
  )
    return [];
  // A repeated ID is ambiguous within that document, so it is deliberately
  // absent from its map and cannot become a shared navigation target.
  const maps = segmentSets.map(uniqueStableAlignmentMap);
  if (maps.some((map) => map.size === 0)) return [];
  return [...maps[0].entries()]
    .filter(([key]) => maps.slice(1).every((map) => map.has(key)))
    .map(([key]) => ({ key, label: commonSectionPresentation(items, key, locale).text }));
}

export function commonSectionPresentation(items, key, locale = "th") {
  const candidates = items.map((item) => {
    const segments = item?.bundle?.segments || [];
    const segment = uniqueStableAlignmentMap(segments).get(key);
    return { segment, language: item?.bundle?.expression?.language || "th" };
  }).filter((item) => item.segment);
  const selected = candidates.find((item) => locale === "en" && item.language === "en") || candidates[0];
  return sectionPresentation(selected?.segment, {
    locale,
    language: selected?.language || "th",
  });
}
